import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Zap, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'register');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    consentAccepted: false,
  });
  
  const { login, register, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      if (user?.is_platform_admin) {
        navigate('/backoffice');
      } else if (user?.has_org) {
        navigate('/app');
      } else {
        navigate('/onboarding');
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const userData = await login(formData.email, formData.password);
        toast.success('Welcome back!');
        
        if (userData.is_platform_admin) {
          navigate('/backoffice');
        } else if (userData.has_org) {
          navigate('/app');
        } else {
          navigate('/onboarding');
        }
      } else {
        if (formData.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        if (!formData.consentAccepted) {
          toast.error('You must accept the Terms & Conditions and Privacy Policy');
          setLoading(false);
          return;
        }
        
        await register(formData.email, formData.password, formData.name, formData.consentAccepted);
        toast.success('Account created successfully!');
        navigate('/onboarding');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 bg-blueprint flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-4">
            <Zap className="w-10 h-10 text-orange-500" />
            <span className="font-black text-3xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              BIDSENTRY
            </span>
          </div>
          <p className="text-slate-600">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-sm border border-slate-200 shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                  className="mt-2"
                  data-testid="auth-name-input"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@company.co.uk"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-2"
                data-testid="auth-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder={isLogin ? '••••••••' : 'Min 8 characters'}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={8}
                className="mt-2"
                data-testid="auth-password-input"
              />
            </div>

            {!isLogin && (
              <div className="flex items-start gap-2" data-testid="consent-checkbox-container">
                <input
                  type="checkbox"
                  id="consent"
                  checked={formData.consentAccepted}
                  onChange={(e) => setFormData({ ...formData, consentAccepted: e.target.checked })}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                  data-testid="consent-checkbox"
                />
                <label htmlFor="consent" className="text-sm text-slate-600">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline underline-offset-2">Terms &amp; Conditions</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:text-orange-700 underline underline-offset-2">Privacy Policy</a>
                </label>
              </div>
            )}
            
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={loading}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </>
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-slate-600 hover:text-orange-600 transition-colors"
              data-testid="auth-toggle-mode"
            >
              {isLogin 
                ? "Don't have an account? Sign up" 
                : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
        
        <p className="text-center text-xs text-slate-500 mt-6">
          <a href="/terms" className="hover:text-orange-600 transition-colors">Terms</a>
          {' | '}
          <a href="/privacy" className="hover:text-orange-600 transition-colors">Privacy</a>
          {' | '}
          <a href="/cookies" className="hover:text-orange-600 transition-colors">Cookies</a>
        </p>
      </div>
    </div>
  );
}
